import { useSelector } from 'react-redux';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetHistoryQuery } from '../services/historyApi';
import { useGetListsQuery } from '../services/bookmarksApi';
import { useGetMyXPQuery } from '../services/xpApi';
import { useGetMyWatchStatsQuery } from '../services/watchTimeApi';
import XPProgressBar from '../components/XPProgressBar';
import RoleBadge, { getPrimaryRole } from '../components/RoleBadge';
import styles from '../css/Profile.module.css';

const getInitials = (name = '') =>
  name.split(/[_\s]/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');

const Avatar = ({ url, login }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  if (!login) return <div className={styles.avatarSkeleton} />;

  return (
    <div className={styles.avatar}>
      {url ? (
        <>
          {!imgLoaded && <div className={styles.avatarImgSkeleton} />}
          <img
            src={url}
            alt=""
            onLoad={() => setImgLoaded(true)}
            style={{ display: imgLoaded ? 'block' : 'none' }}
          />
        </>
      ) : (
        getInitials(login)
      )}
    </div>
  );
};

const Profile = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { data: historyData, isLoading: historyLoading } = useGetHistoryQuery(
    { page: 1, perPage: 1 },
    { skip: !isAuthenticated }
  );
  const { data: listsData, isLoading: listsLoading } = useGetListsQuery(
    undefined,
    { skip: !isAuthenticated }
  );
  const { data: xpData, isLoading: xpLoading, isError: xpError } = useGetMyXPQuery(
    undefined,
    { skip: !isAuthenticated }
  );
  const { data: watchStatsData, isLoading: watchStatsLoading } = useGetMyWatchStatsQuery(
    undefined,
    { skip: !isAuthenticated }
  );

  const statsLoading = historyLoading || listsLoading || xpLoading || watchStatsLoading;
  const historyCount = historyData?.meta?.total ?? 0;
  const bookmarksCount = listsData?.data?.reduce((sum, list) => sum + (list.items_count ?? 0), 0) ?? 0;
  const xpProgress = xpData?.data ?? null;

  if (!isAuthenticated) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.guest}>
          <div className={styles.guestIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <p className={styles.guestText}>Войдите в аккаунт, чтобы видеть историю просмотров</p>
          <Link to="/auth/login" className={styles.loginBtn}>Войти</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profilePage}>

      {/* ── Avatar + Name ── */}
      <div className={styles.hero}>
        <Avatar url={user?.avatar_url} login={user?.login} />
        {user?.login
          ? <h1 className={styles.login}>{user.login}<RoleBadge role={getPrimaryRole(user?.roles)} /></h1>
          : <div className={styles.loginSkeleton} />
        }
        <Link to="/settings" className={styles.settingsBtn} aria-label="Настройки">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </Link>
      </div>

      {/* ── XP Progress ── */}
      <XPProgressBar xpData={xpProgress} isLoading={xpLoading} isError={xpError} />

      {/* ── Stats ── */}
      <div className={styles.stats}>
        <div className={styles.statItem}>
          {statsLoading
            ? <span className={styles.statNumSkeleton} />
            : <span className={styles.statNum}>{watchStatsData?.data?.total_minutes ?? 0}</span>
          }
          <span className={styles.statLabel}>Минуты</span>
        </div>
        <div className={styles.divider} />
        <div className={styles.statItem}>
          {statsLoading
            ? <span className={styles.statNumSkeleton} />
            : <span className={styles.statNum}>{historyCount}</span>
          }
          <span className={styles.statLabel}>История</span>
        </div>
        <div className={styles.divider} />
        <div className={styles.statItem}>
          {statsLoading
            ? <span className={styles.statNumSkeleton} />
            : <span className={styles.statNum}>{bookmarksCount}</span>
          }
          <span className={styles.statLabel}>Закладки</span>
        </div>
      </div>

      {/* ── Quick Links ── */}
      <div className={styles.links}>
        <Link to="/history" className={styles.linkItem}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          История просмотров
          <svg className={styles.arrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </Link>
        <Link to="/bookmarks" className={styles.linkItem}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
          </svg>
          Закладки
          <svg className={styles.arrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </Link>
        <Link to="/settings" className={styles.linkItem}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          Настройки
          <svg className={styles.arrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </Link>
      </div>

    </div>
  );
};

export default Profile;
